import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';
import {
  MeetingAutoSaveData,
  UseMeetingAutoSaveParams,
  UseMeetingAutoSaveReturn,
  MeetingChangeDetection,
  DEFAULT_MEETING_AUTO_SAVE_DATA,
  PartialMeetingAutoSaveData,
} from '../types/meetingAutoSave';

const MEETING_AUTO_SAVE_PAGE_KEY = EDIT_PAGE_KEY_MAP.studio_board_governance_meeting;

/**
 * Custom hook for Meeting Auto-Save functionality with separate state tracking
 * 
 * This hook implements auto-save for meeting modals while avoiding infinite loops
 * by maintaining a separate auto-save state that doesn't interfere with the main
 * editingMeeting state. It follows the established pattern from useBoardGovernanceAutoSave.
 * 
 * Key Features:
 * - Separate auto-save state variable to prevent infinite loops
 * - onDataChange callback for tracking modal changes
 * - Consistent with existing auto-save hooks in the codebase
 * - Maintains editingMeeting usage for display and API operations
 * 
 * @param params - Hook parameters including meetingId, modalVisible, and editingMeeting
 * @returns Auto-save handlers and state
 */
export const useMeetingAutoSave = ({
  meetingId,
  modalVisible,
  editingMeeting,
  setEditingMeeting,
}: UseMeetingAutoSaveParams): UseMeetingAutoSaveReturn => {
  // Auto-save modal visibility state
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  
  // Separate auto-save data state - this is the key to preventing infinite loops
  const [autoSaveData, setAutoSaveData] = useState<MeetingAutoSaveData>(DEFAULT_MEETING_AUTO_SAVE_DATA);
  
  // Page key for auto-save - use dedicated key with meeting ID suffix for separate storage per meeting
  const meetingPageKey = useMemo(() => {
    return meetingId 
      ? `${MEETING_AUTO_SAVE_PAGE_KEY}_${meetingId}`
      : '';
  }, [meetingId]);

  // Auto-save methods from the central auto-save system
  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey
  } = useAutoSaveParams({
    pageKey: meetingPageKey,
  });

  // Track the initial snapshot when modal opens (capture once per modal session)
  const initialSnapshotRef = useRef<MeetingAutoSaveData | null>(null);

  // Capture initial snapshot when modal opens
  useEffect(() => {
    if (modalVisible && meetingId && editingMeeting && !initialSnapshotRef.current) {
      // Capture initial state once when modal first opens
      const initialData: MeetingAutoSaveData = {
        summary_title: editingMeeting.summary_title || '',
        description: editingMeeting.description || '',
        date: editingMeeting.date || null,
      };
      
      initialSnapshotRef.current = initialData;
      setAutoSaveData(initialData);
      
      console.log('🎯 [MEETING AUTO-SAVE] Captured initial snapshot:', initialData);
    } else if (!modalVisible) {
      // Reset snapshot and auto-save data when closing modal
      initialSnapshotRef.current = null;
      setAutoSaveData(DEFAULT_MEETING_AUTO_SAVE_DATA);
    }
  }, [modalVisible, meetingId, editingMeeting]);

  // Current save params for auto-save comparison - only when modal is visible
  const currentSaveParams = useMemo(() => {
    if (!modalVisible || !meetingId) {
      // When modal is not visible, return empty params to prevent auto-save from triggering
      return DEFAULT_MEETING_AUTO_SAVE_DATA;
    }

    return autoSaveData;
  }, [modalVisible, meetingId, autoSaveData]);

  // Calculate initialParams for auto-save - use snapshot if available
  const initialParams = useMemo(() => {
    if (initialSnapshotRef.current) {
      return initialSnapshotRef.current;
    }
    
    // If no snapshot yet, use current editingMeeting data
    if (modalVisible && meetingId && editingMeeting) {
      return {
        summary_title: editingMeeting.summary_title || '',
        description: editingMeeting.description || '',
        date: editingMeeting.date || null,
      };
    }
    
    // Default empty state
    return DEFAULT_MEETING_AUTO_SAVE_DATA;
  }, [modalVisible, meetingId, editingMeeting]);

  // Use auto-save hook with separate state
  useAutoSaveParams({
    pageKey: meetingPageKey,
    initialParams: initialParams,
    saveParamsCur: currentSaveParams,
    isBegin: modalVisible && !!meetingId,
    isChangeValue: true,
  });

  // Change detection utility
  const detectChanges = useCallback((
    current: MeetingAutoSaveData, 
    initial: MeetingAutoSaveData
  ): MeetingChangeDetection => {
    const summaryChanged = current.summary_title !== initial.summary_title;
    const descriptionChanged = current.description !== initial.description;
    const dateChanged = current.date !== initial.date;
    
    return {
      summaryChanged,
      descriptionChanged,
      dateChanged,
      hasAnyChanges: summaryChanged || descriptionChanged || dateChanged,
    };
  }, []);

  // Calculate hasUnsavedChanges for modal close warning
  const hasUnsavedChanges = useMemo(() => {
    if (!modalVisible || !meetingId || !initialSnapshotRef.current) return false;
    
    const changes = detectChanges(autoSaveData, initialSnapshotRef.current);
    return changes.hasAnyChanges;
  }, [modalVisible, meetingId, autoSaveData, detectChanges]);

  // onDataChange callback - this is the key function that updates auto-save state
  // without affecting editingMeeting, thus preventing infinite loops
  const onDataChange = useCallback((data: PartialMeetingAutoSaveData) => {
    if (!modalVisible || !meetingId) return;
    
    setAutoSaveData(prevData => {
      const newData = { ...prevData, ...data };
      console.log('📝 [MEETING AUTO-SAVE] Data changed:', data, 'New state:', newData);
      return newData;
    });
  }, [modalVisible, meetingId]);

  // Handler: Check for auto-save before opening modal
  // Accepts optional meetingIdOverride to handle async state updates
  const handleMeetingModalEdit = useCallback((originalOpenHandler: () => void, meetingIdOverride?: string | null) => {
    // Use override if provided (for cases where state hasn't updated yet)
    const effectiveMeetingId = meetingIdOverride !== undefined ? meetingIdOverride : meetingId;
    const effectivePageKey = effectiveMeetingId 
      ? `${MEETING_AUTO_SAVE_PAGE_KEY}_${effectiveMeetingId}`
      : '';
    
    console.log('🔍 [MEETING AUTO-SAVE] Checking for auto-save:', { 
      effectivePageKey, 
      meetingIdOverride, 
      meetingId,
      hasAutoSave: effectivePageKey ? showRestorePrompt(effectivePageKey) : false 
    });
    
    if (effectivePageKey && showRestorePrompt(effectivePageKey)) {
      setAutoSaveVisible(true);
    } else {
      originalOpenHandler();
    }
  }, [showRestorePrompt, meetingId]);

  // Handler: Discard auto-save and open modal
  const handleAutoSaveCancel = useCallback((originalOpenHandler: () => void) => {
    setAutoSaveVisible(false);
    if (meetingPageKey) {
      deleteSaveParamsByPageKey(meetingPageKey);
    }
    originalOpenHandler();
  }, [deleteSaveParamsByPageKey, meetingPageKey]);

  // Handler: Restore from auto-save
  const handleAutoSaveConfirm = useCallback((originalOpenHandler: () => void) => {
    const saveValue = getSaveParamsValue(meetingPageKey) || {};

    console.log('🔄 [MEETING AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

    setAutoSaveVisible(false);

    // Update auto-save data with restored values
    const restoredData: MeetingAutoSaveData = {
      summary_title: saveValue.summary_title !== undefined ? saveValue.summary_title : autoSaveData.summary_title,
      description: saveValue.description !== undefined ? saveValue.description : autoSaveData.description,
      date: saveValue.date !== undefined ? saveValue.date : autoSaveData.date,
    };
    
    setAutoSaveData(restoredData);

    // Update editingMeeting with restored data so the modal displays the restored values
    if (editingMeeting && setEditingMeeting) {
      setEditingMeeting({
        ...editingMeeting,
        summary_title: saveValue.summary_title !== undefined ? saveValue.summary_title : editingMeeting.summary_title,
        description: saveValue.description !== undefined ? saveValue.description : editingMeeting.description,
        date: saveValue.date !== undefined ? saveValue.date : editingMeeting.date,
      });
    }

    // Open modal after state updates
    setTimeout(() => {
      originalOpenHandler();
    }, 100);
  }, [getSaveParamsValue, meetingPageKey, autoSaveData, editingMeeting, setEditingMeeting]);

  // Clear auto-save after successful save
  const clearMeetingModalAutoSave = useCallback(() => {
    if (meetingPageKey) {
      deleteSaveParamsByPageKey(meetingPageKey);
    }
    // Reset auto-save data to initial state
    setAutoSaveData(DEFAULT_MEETING_AUTO_SAVE_DATA);
  }, [deleteSaveParamsByPageKey, meetingPageKey]);

  return {
    // State
    autoSaveVisible,
    meetingPageKey,
    autoSaveData,

    // Core callback for modal integration
    onDataChange,

    // Handlers
    handleMeetingModalEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearMeetingModalAutoSave,

    // Comparison result for unsaved changes warning
    hasUnsavedChanges,
  };
};
